import React, { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { PencilIcon, UserIcon } from 'lucide-react';

import { queryClient } from 'api';
import { EventOrganizer } from 'types/event_organizers.types';
import { useToaster, ToasterVariant } from 'hooks/useToaster';
import { cn } from '@epoch-ui/lib/utils';
import { Avatar, AvatarImage, AvatarFallback } from '@epoch-ui/components/Avatar';
import { Button } from '@epoch-ui/components/Button';
import { eventEditorQueryKeys } from '@event-editor/queryKeys';
import { eventOrganizersApi } from '@event-editor/api/eventOrganizers';
import { useEditableEvent } from '@event-editor/hooks/useEditableEvent';

import { EditEventOrganizerModal } from './EditEventOrganizerModal';

const EditEventOrganizer: React.FC = () => {
  const event = useEditableEvent();
  const { showToast } = useToaster();

  const [editModalOpen, setEditModalOpen] = useState(false);

  const isCancelled = !!event.scheduledDeletionAt;
  const isDisabled = event.hasEnded || isCancelled;

  const updateEventOrganizerMutation = useMutation({
    mutationFn: (organizer: EventOrganizer) =>
      eventOrganizersApi.update({
        eventId: event.id!,
        name: organizer.name,
        email: organizer.email
      }),
    onSuccess: (result) => {
      if (result.ok) {
        queryClient.invalidateQueries({ queryKey: eventEditorQueryKeys.event(event.id!) });
        setEditModalOpen(false);
      } else {
        showToast('Failed to update event organizer.', { variant: ToasterVariant.ERROR });
      }
    },
    onError: (error) => {
      console.error('Failed to update event organizer:', error);
      showToast('Failed to update event organizer.', { variant: ToasterVariant.ERROR });
    }
  });

  return (
    <div className={cn('tw-flex tw-flex-col tw-items-start', isDisabled && 'tw-opacity-40')}>
      {/* Heading  */}
      <h2 className="tw-mb-5 tw-text-xl tw-font-medium tw-leading-7">Organizer</h2>

      {/* Organizer */}
      <div className="tw-mb-4 tw-flex tw-flex-row tw-items-center tw-gap-3">
        <Avatar>
          <AvatarImage src={event.organizer?.imageUrl} />
          <AvatarFallback>
            {event.organizer?.name?.charAt(0) || <UserIcon size={24} />}
          </AvatarFallback>
        </Avatar>
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-[2px] tw-text-sm tw-leading-4">
          <span className="tw-text-sm tw-font-medium">{event.organizer?.name}</span>
          <span className="tw-text-xs tw-font-normal tw-text-muted-foreground">
            {event.organizer?.email}
          </span>
        </div>
      </div>

      <Button
        variant="secondary"
        size="xxs"
        className="tw-rounded-xl disabled:tw-opacity-100"
        disabled={isDisabled}
        onClick={() => setEditModalOpen(true)}
      >
        <PencilIcon size={16} className="tw-mr-1" />
        Edit organizer
      </Button>

      <EditEventOrganizerModal
        open={editModalOpen}
        onOpenChange={setEditModalOpen}
        onSubmit={(selectedOrganizer) => {
          updateEventOrganizerMutation.mutate(selectedOrganizer!);
        }}
      />
    </div>
  );
};

export { EditEventOrganizer };
